import type {
  FiasAddressResponse,
  FiasAddressValue,
  FiasDivision,
  FiasId,
  FiasFieldsSettings,
} from "./common.ts";
import {FiasSchema} from "./common.js";
import {FiasFields, FiasSuggestLevels, FIAS_API_PREFIX} from "./common.js";
import {FiasData} from "./FiasData.js";
import {filterVisibleFields, getFieldText, MAIN_FIELDS} from "./common.js";
import type {OperationResult} from "@skbkontur/operation-result";

export interface FiasSearchQuery {
  [key: string]: string | number | boolean | FiasId | FiasFields[] | undefined;
  address?: string | undefined;
  prefix?: string | undefined;
  fiasId?: FiasId | undefined;
  parentFiasId?: FiasId | undefined;
  levels?: FiasFields[];
  fullAddress?: boolean | undefined;
  directParent?: boolean | undefined;
  limit?: number | undefined;
  actual?: boolean | undefined;
  version?: string | undefined;
  division?: FiasDivision | undefined;
  schema?: FiasSchema | undefined;
  suggestUnknownParts?: boolean | undefined;
  suggestUnknownLevel?: FiasSuggestLevels | undefined;
  searchSteads?: boolean;
}

export const createQuery = (query: FiasSearchQuery): string => {
  const params = [];
  for (const key in query) {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      const param = query[key];
      if (param !== undefined) {
        if (key === 'levels' && Array.isArray(param)) {
          for (const level of param) {
            params.push(`level[]=${encodeURIComponent(level)}`);
          }
        } else {
          params.push(`${key}=${encodeURIComponent(`${param}`)}`);
        }
      }
    }
  }
  return params.join('&');
};

type Suggest = { strict: boolean; fiasId: string | null; value: string };
type AddressResult<T extends Suggest | undefined = undefined> = OperationResult<{ errorKind: "noData" | "error"; error: Error; response?: FiasAddressResponse | undefined }, { data: FiasAddressValue; suggest: T }>;

export const getAddressPathByFiasId = (fiasId: string, query: FiasSearchQuery) => {
  return `addresses/structural/${fiasId}?${createQuery(query)}`;
};
const handleFiasApiResponse = async <T extends Suggest | undefined = undefined>({ data, notFoundMessage, fieldsSettings }: { data: Response, notFoundMessage: string, fieldsSettings?: FiasFieldsSettings | undefined }): Promise<AddressResult<T>> => {
  if (data.ok) {
    const json: FiasAddressResponse | FiasAddressResponse[] = await data.json();
    const response  = Array.isArray(json) ? json[0] : json;
    const { suggest } = response || {};
    if (!response || (suggest && !["MATCH", "NOT_STRICT_MATCH"].includes(suggest.matchResult))) {
      return { success: false, fault: { errorKind: "noData", error: new Error(notFoundMessage), response } };
    }
    const responseFiltered = fieldsSettings ? filterVisibleFields(response, fieldsSettings) : response;
    const address = MAIN_FIELDS.reduce<FiasAddressValue>((acc, field) => {
      const fiasObject = responseFiltered?.[field];

      if (fiasObject) {
        const data: FiasData = new FiasData(fiasObject);
        const { name } = data;

        return {
          ...acc,
          [field]: {
            name,
            text: getFieldText({ field, data, name }),
            data: fiasObject,
          },
        };
      }

      return acc;
    }, {});

    return {
      success: true,
      value: {
        data: address,
        suggest: suggest ? { fiasId: suggest.fiasId, strict: suggest.matchResult === "MATCH", value: suggest.value } as T : undefined as T,
      },
    };
  }

  return { success: false, fault: { errorKind: "error", error: new Error(`Fetch error with status: ${data.status}`) } };
};
interface GetAddressQueryParams {
  version?: string;
  division: FiasDivision;
  schema: FiasSchema;
  suggestUnknownLevel?: FiasSuggestLevels;
}
export const getAddressByFiasId = async (
  {
    baseUrl,
    fiasId,
    query,
    fieldsSettings,
  }: { baseUrl: string; fiasId: string; query: Omit<GetAddressQueryParams, "suggestUnknownLevel">; fieldsSettings?: FiasFieldsSettings },
): Promise<OperationResult<{ errorKind: "noData" | "error"; error: Error }, { data: FiasAddressValue }>> => {
  const queryCompleted: FiasSearchQuery = {
    division: query.division,
    schema: query.schema,
  };
  const path = getAddressPathByFiasId(fiasId, queryCompleted);
  const baseUrlVersioned = new URL(baseUrl);
  baseUrlVersioned.pathname = FIAS_API_PREFIX;
  const data = await fetch(`${baseUrlVersioned.toString()}${path}`, {
    method: 'GET',
  });

  return handleFiasApiResponse({ data, notFoundMessage: `No data found for fias id: ${fiasId}`, fieldsSettings });
};
export const getAddressPathByString = (query: FiasSearchQuery) => {
  return `addresses/search?${createQuery({ limit: 1, level: FiasFields.apartment, searchSteads: true, ...query })}`;
};
export const getAddressByString = async (
  {
    baseUrl,
    addressString,
    query,
    fieldsSettings,
  }: { baseUrl: string; addressString: string; query: GetAddressQueryParams; fieldsSettings?: FiasFieldsSettings },
): Promise<AddressResult<Suggest>> => {
  const queryCompleted: FiasSearchQuery = {
    division: query.division,
    schema: query.schema,
    address: addressString,
    version: query?.version || undefined,
    suggestUnknownLevel: query?.suggestUnknownLevel || undefined,
  };
  const path = getAddressPathByString(queryCompleted);
  const baseUrlVersioned = new URL(baseUrl);
  baseUrlVersioned.pathname = FIAS_API_PREFIX;
  const data = await fetch(`${baseUrlVersioned.toString()}${path}`, {
    method: 'GET',
  });

  return handleFiasApiResponse<Suggest>({ data, notFoundMessage: `No data found for string "${addressString}"`, fieldsSettings });
}
